import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { User, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      setAuth(response.data);
      
      if (response.data.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/employee');
      }
      
      toast.success('Успешен вход');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Грешка при вход');
    } finally {
      setLoading(false);
    }
  };

  if (!role) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative">
        {/* Admin button in top right */}
        <Button
          data-testid="admin-login-button"
          onClick={() => setRole('admin')}
          variant="outline"
          className="absolute top-6 right-6 border-2 border-slate-300 hover:border-red-600 hover:bg-red-50 transition-all group"
        >
          <Shield className="w-5 h-5 mr-2 text-slate-600 group-hover:text-red-600 transition-colors" />
          <span className="font-semibold uppercase text-sm">Администратор</span>
        </Button>

        <div className="w-full max-w-md">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <img 
                src="/garant-logo-opt.png" 
                alt="ГАРАНТ АД" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <h1 className="heading-font text-5xl font-bold text-slate-900 uppercase tracking-tight mb-2">
              ГАРАНТ АД
            </h1>
            <p className="text-lg text-slate-600">Производствена система</p>
          </div>

          <Card
            data-testid="employee-login-card"
            onClick={() => setRole('employee')}
            className="p-10 border-2 hover:border-red-600 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-28 h-28 bg-slate-100 rounded-sm flex items-center justify-center group-hover:bg-red-50 transition-colors">
                <User className="w-14 h-14 text-slate-600 group-hover:text-red-600 transition-colors" />
              </div>
              <div>
                <h2 className="heading-font text-4xl font-bold text-slate-900 uppercase mb-2">
                  Вход
                </h2>
                <p className="text-slate-600">За служители</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex justify-center mb-6">
          {role === 'admin' ? (
            <Shield className="w-12 h-12 text-red-600" />
          ) : (
            <User className="w-12 h-12 text-red-600" />
          )}
        </div>

        <h2 className="heading-font text-3xl font-bold text-center text-slate-900 uppercase mb-8">
          {role === 'admin' ? 'Администратор' : 'Вход'}
        </h2>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label htmlFor="username" className="text-sm uppercase tracking-wide font-medium mb-2 block">
              Потребителско име
            </Label>
            <Input
              data-testid="login-username-input"
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-14 text-lg border-2 focus:border-red-600"
              autoComplete="off"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-sm uppercase tracking-wide font-medium mb-2 block">
              Парола
            </Label>
            <div className="relative">
              <Input
                data-testid="login-password-input"
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 text-lg border-2 focus:border-red-600 pr-12"
                autoComplete="off"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 h-14 text-lg font-bold uppercase tracking-wide"
            >
              {loading ? 'Зареждане...' : 'Вход'}
            </Button>

            <Button
              data-testid="login-back-button"
              type="button"
              onClick={() => setRole(null)}
              variant="outline"
              className="w-full h-12 border-2 border-slate-200 hover:border-slate-400 font-semibold uppercase"
            >
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default Login;